import React, { FC, useMemo } from "react";
import StyledDialog from "@components/StyledDialog";
import CharacterItem from "@components/CharactersForm/CharacterItem";
import { Person } from "@models/person";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import StyledLink from "@components/StyledLink/StyledLink";
import { Character } from "@models/character";
import { containsIgnoreCase } from "@utils/string.util";

const PersonSelectDialog: FC<{
  onClose: () => void;
  open: boolean;
  persons: Person[];
  characters: Character[];
  onPersonSelected: (person: Person) => void;
}> = ({ onClose, open, persons, characters, onPersonSelected }) => {
  const { watch, register } = useForm<{ search: string }>();
  const { t } = useTranslation();
  const searchStr = watch("search");

  const filteredPersons = useMemo(
    () =>
      persons.filter((person: Person) =>
        [person.nickname, person.firstName, person.lastName].some((value) =>
          containsIgnoreCase(value, searchStr),
        ),
      ),
    [persons, searchStr],
  );

  return (
    <StyledDialog onClose={onClose} open={open}>
      <Box display="flex" gap="16px" flexDirection="column" minHeight="50vh">
        <TextField
          {...register(`search`)}
          type="text"
          size="small"
          variant="outlined"
          placeholder={t("characters.dialog.searchInput.placeholder")}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box display="flex" gap="8px">
          <Typography
            fontSize="14px"
            lineHeight="22px"
            fontWeight="400"
            color="#171A1FFF"
          >
            {t("characters.dialog.noMatchingPerson")}
          </Typography>

          <StyledLink to="/patient-info/family-members">
            {t("characters.dialog.createPersonButton")}
          </StyledLink>
        </Box>

        <Box display="flex" gap="16px" flexWrap="wrap" paddingTop="12px">
          {filteredPersons.map((person) => (
            <CharacterItem
              key={person.id}
              person={person}
              character={characters.find((c) => c.person?.id === person.id)}
              onChange={() => onPersonSelected(person)}
            />
          ))}
        </Box>
      </Box>
    </StyledDialog>
  );
};

export default PersonSelectDialog;
